import React, { useEffect, useState } from 'react'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AnnouncementModal from '../../components/AnnouncementModal'
import useAnnouncements from '../../hooks/announcements/useAnnouncements'
import useCreateAnnouncement from '../../hooks/announcements/useCreateAnnouncement'
import useDeleteAnnouncement from '../../hooks/announcements/useDeleteAnnouncement'
import useUpdateAnnouncement from '../../hooks/announcements/useUpdateAnnouncement'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead' 
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import TableBody from '@mui/material/TableBody'
import TablePagination from '@mui/material/TablePagination'
import Box from '@mui/material/Box'
import { formatDate } from '../../utils/helpers'
import IconButton from '@mui/material/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }

  if (b[orderBy] > a[orderBy]) {
    return 1
  }

  return 0
}

const getComparator = (order) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, 'EndDate')
    : (a, b) => -descendingComparator(a, b, 'EndDate')
}

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const Announcements = () => {
  const [modalAction, setModalAction] = useState('Add')
  const [modalOpen, setModalOpen] = useState(false)
  const [announcementId, setAnnouncementId] = useState(0)
  const [order, setOrder] = useState('desc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'info',
    message: ''
  })

  const { data: announcements, isSuccess } = useAnnouncements()
  const createAnnouncement = useCreateAnnouncement()
  const deleteAnnouncement = useDeleteAnnouncement()
  const updateAnnouncement = useUpdateAnnouncement()

  const handleOpenModal = (action, id = null) => {
    setModalAction(action)
    setModalOpen(true)

    if (id) {
      setAnnouncementId(id)
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
  }

  const handleRequestSort = () => {
    const isAsc = order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const emptyRows = isSuccess ?
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - announcements.length) : 0
    : 0

  useEffect(() => {
    if (createAnnouncement.isSuccess) {
      setModalOpen(false)
      setAlertState({
        show: true,
        variant: 'success',
        message: 'Added a new announcement.'
      })
    }
  }, [createAnnouncement.isSuccess])

  useEffect(() => {
    if (updateAnnouncement.isSuccess) {
      setModalOpen(false)
      setAlertState({
        show: true,
        variant: 'success',
        message: 'Updated an announcement.'
      })
    }
  }, [updateAnnouncement.isSuccess])

  const handleDeleteAnnouncement = (id) => {
    deleteAnnouncement.mutate(id)
  }

  useEffect(() => {
    if (deleteAnnouncement.isSuccess) {
      setAlertState({
        show: true,
        variant: 'success',
        message: 'Deleted an announcement.'
      })
    }

    if (deleteAnnouncement.isError) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Unable to delete the announcement.'
      })
    }
  }, [deleteAnnouncement.isSuccess, deleteAnnouncement.isError])

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography
            variant='h4'
            sx={{
              color: '#27417A',
              fontWeight: 'bold'
            }}
          >
            Announcements
          </Typography>
        </Grid>
        <Grid 
          item
          xs={6}
          sx={{
            textAlign: 'right' 
          }}
        >
          <Button 
            variant='contained'
            sx={{
              backgroundColor: '#3A6DAA'
            }}
            onClick={() => handleOpenModal('Add')}
          >
            Add Announcement
          </Button>
        </Grid>
        <Grid 
          item
          xs={12}
        >
          {
            isSuccess && (
              <>
                {
                  announcements.length > 0 ? (
                    <>
                      {
                        alertState.show && (
                          <Alert severity={alertState.variant} onClose={() => setAlertState((ps) => ({ ...ps, show: false }))}>
                            {alertState.message}
                          </Alert>
                        )
                      }
                      <Table
                        aria-labelledby='announcementTable'
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              Title
                            </TableCell>
                            <TableCell>
                              Description
                            </TableCell>
                            <TableCell>
                              Link
                            </TableCell>
                            <TableCell
                              sortDirection={order}
                            >
                              <TableSortLabel
                                active
                                direction={order}
                                onClick={handleRequestSort}
                              >
                                End Date
                                <Box 
                                  component='span' 
                                  sx={{
                                    border: 0,
                                    clip: 'rect(0 0 0 0)',
                                    height: '1 px',
                                    margin: -1,
                                    overflow: 'hidden',
                                    padding: 0,
                                    position: 'absolute',
                                    whiteSpace: 'nowrap',
                                    width: '1px'
                                  }}
                                >
                                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align='center'>
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            stableSort(announcements, getComparator(order))
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((row) => (
                                <TableRow
                                  hover
                                  tabIndex={-1}
                                  key={`announcement-${row.Id}`}
                                >
                                  <TableCell>
                                    {row.Title}
                                  </TableCell>
                                  <TableCell>
                                    {row.Description}
                                  </TableCell>
                                  <TableCell>
                                    {row.Link}
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(row.EndDate)}
                                  </TableCell>
                                  <TableCell>
                                    <Stack spacing={2} direction='row'>
                                      <IconButton 
                                        color='warning' 
                                        size='small'
                                        onClick={() => handleOpenModal('Edit', row.Id)}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton 
                                        color='error' 
                                        size='small' 
                                        onClick={() => handleDeleteAnnouncement(row.Id)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))
                          }
                          {
                            emptyRows > 0 && (
                              <TableRow
                                style={{
                                  height: 53 * emptyRows
                                }}
                              >
                                <TableCell colSpan={5} />
                              </TableRow>
                            )
                          }
                        </TableBody>
                      </Table>
                      <TablePagination 
                        rowsPerPageOptions={[5, 10, 25]}
                        component='div'
                        count={announcements.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </>
                  )
                    : <Alert severity="info">No Announcements</Alert>
                }
              </>
            )
          }
        </Grid>
      </Grid>
      <AnnouncementModal
        action={modalAction}
        open={modalOpen}
        onClose={handleCloseModal}
        createAnnouncement={createAnnouncement}
        updateAnnouncement={updateAnnouncement}
        announcements={announcements}
        announcementId={announcementId}
      />
    </div>
  )
}

export default Announcements